import { useState } from "react";
import { CsvRow } from "./parseContentCsv";
import { RowIssue } from "./validateContentRow";

interface CsvPreviewTableProps {
  rows: CsvRow[];
  /** Validation issues per row index; absent or empty means the row is submittable. */
  issuesByIndex: Record<number, RowIssue[]>;
  /** Row indexes that repeat an earlier row's title + author. */
  duplicateIndexes: Set<number>;
  /** Row indexes the user has kept for submission. */
  selected: Set<number>;
  onToggle: (index: number) => void;
  onToggleAll: (select: boolean) => void;
  disabled?: boolean;
}

const TEXT_PREVIEW_LENGTH = 120;

/**
 * The review step: every parsed row, why it would be rejected, and whether it is
 * going to be submitted.
 *
 * Invalid rows are shown rather than dropped — a row missing from a preview reads as
 * a parsing bug, whereas a row with "Required." under an empty cell is a fixable
 * mistake in the spreadsheet.
 */
export default function CsvPreviewTable({
  rows,
  issuesByIndex,
  duplicateIndexes,
  selected,
  onToggle,
  onToggleAll,
  disabled = false,
}: CsvPreviewTableProps) {
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const toggleExpanded = (index: number) => {
    const next = new Set(Array.from(expanded));
    if (next.has(index)) next.delete(index);
    else next.add(index);
    setExpanded(next);
  };

  const validRows = rows.filter((row) => (issuesByIndex[row.index] ?? []).length === 0);
  const allValidSelected =
    validRows.length > 0 && validRows.every((row) => selected.has(row.index));

  const issueFor = (index: number, field: string) =>
    (issuesByIndex[index] ?? []).find((issue) => issue.field === field)?.message;

  const cellClass = (index: number, field: string) =>
    `px-3 py-2 align-top ${issueFor(index, field) ? "text-red-600" : "text-dark-grey"}`;

  return (
    <div className="overflow-x-auto rounded-xl border-2 border-gray-200">
      <div className="max-h-[26rem] overflow-y-auto">
        <table className="min-w-full text-xs sm:text-sm">
          <thead className="sticky top-0 z-10 bg-off-white">
            <tr className="text-left text-dark-grey">
              <th scope="col" className="px-3 py-2 w-10">
                <input
                  type="checkbox"
                  aria-label="Select all valid rows"
                  className="rounded border-gray-300 text-orange focus:ring-orange"
                  checked={allValidSelected}
                  disabled={disabled || validRows.length === 0}
                  onChange={(event) => onToggleAll(event.target.checked)}
                />
              </th>
              <th scope="col" className="px-3 py-2 w-12 font-semibold">#</th>
              <th scope="col" className="px-3 py-2 font-semibold">Type</th>
              <th scope="col" className="px-3 py-2 font-semibold">Title</th>
              <th scope="col" className="px-3 py-2 font-semibold">Author</th>
              <th scope="col" className="px-3 py-2 font-semibold">Text</th>
              <th scope="col" className="px-3 py-2 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((row) => {
              const issues = issuesByIndex[row.index] ?? [];
              const valid = issues.length === 0;
              const duplicate = duplicateIndexes.has(row.index);
              const isExpanded = expanded.has(row.index);
              const text = row.values.text.trim();

              return (
                <tr
                  key={row.index}
                  className={
                    !valid
                      ? "bg-red-50"
                      : duplicate
                        ? "bg-yellow/10"
                        : selected.has(row.index)
                          ? "bg-white"
                          : "bg-gray-50"
                  }
                >
                  <td className="px-3 py-2 align-top">
                    <input
                      type="checkbox"
                      aria-label={`Include row ${row.index}`}
                      className="rounded border-gray-300 text-orange focus:ring-orange"
                      checked={selected.has(row.index)}
                      disabled={disabled || !valid}
                      onChange={() => onToggle(row.index)}
                    />
                  </td>
                  <td className="px-3 py-2 align-top text-gray-500">{row.index}</td>

                  <td className={cellClass(row.index, "content_type")}>
                    {row.values.content_type.trim() || "—"}
                    {issueFor(row.index, "content_type") && (
                      <p className="text-red-600 mt-0.5">{issueFor(row.index, "content_type")}</p>
                    )}
                  </td>

                  <td className={`${cellClass(row.index, "title")} max-w-[16rem]`}>
                    <span className="block truncate" title={row.values.title}>
                      {row.values.title.trim() || "—"}
                    </span>
                    {issueFor(row.index, "title") && (
                      <p className="text-red-600 mt-0.5">{issueFor(row.index, "title")}</p>
                    )}
                  </td>

                  <td className={`${cellClass(row.index, "author")} max-w-[10rem]`}>
                    <span className="block truncate" title={row.values.author}>
                      {row.values.author.trim() || "—"}
                    </span>
                    {issueFor(row.index, "author") && (
                      <p className="text-red-600 mt-0.5">{issueFor(row.index, "author")}</p>
                    )}
                  </td>

                  <td className={`${cellClass(row.index, "text")} max-w-[22rem]`}>
                    {text ? (
                      <>
                        <span className={isExpanded ? "whitespace-pre-wrap" : ""}>
                          {isExpanded || text.length <= TEXT_PREVIEW_LENGTH
                            ? text
                            : `${text.slice(0, TEXT_PREVIEW_LENGTH)}…`}
                        </span>
                        {text.length > TEXT_PREVIEW_LENGTH && (
                          <button
                            type="button"
                            onClick={() => toggleExpanded(row.index)}
                            className="ml-1 text-orange font-semibold hover:underline"
                          >
                            {isExpanded ? "less" : "more"}
                          </button>
                        )}
                        <p className="text-gray-400 mt-0.5">{text.length} chars</p>
                      </>
                    ) : (
                      "—"
                    )}
                    {issueFor(row.index, "text") && (
                      <p className="text-red-600 mt-0.5">{issueFor(row.index, "text")}</p>
                    )}
                  </td>

                  <td className="px-3 py-2 align-top">
                    {!valid ? (
                      <span className="inline-block rounded-full bg-red-100 px-2 py-0.5 font-semibold text-red-800">
                        {issues.length} problem{issues.length > 1 ? "s" : ""}
                      </span>
                    ) : duplicate ? (
                      <span className="inline-block rounded-full bg-yellow/30 px-2 py-0.5 font-semibold text-dark-grey">
                        Duplicate
                      </span>
                    ) : (
                      <span className="inline-block rounded-full bg-green-100 px-2 py-0.5 font-semibold text-green-800">
                        Ready
                      </span>
                    )}
                    {(issueFor(row.index, "source_url") || issueFor(row.index, "publish_date")) && (
                      <p className="text-red-600 mt-1">
                        {[
                          issueFor(row.index, "source_url"),
                          issueFor(row.index, "publish_date"),
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      </p>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
